import React from 'react';
import { EnvelopeIcon, UserIcon } from '@heroicons/react/24/outline';

import Link from '@dashboard/components/Link';
import { Button } from '@dashboard/components/ui/button';
import {
  Item,
  ItemAction,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@dashboard/components/ui/item';
import { OrderCard } from './OrderCard';
import { CopyButton } from './OrderDetailsTable';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';

/**
 * The confirmation email's customer card. Hidden without a customer unless `alwaysShow`, which the
 * submission page uses to keep its "Not provided" card.
 */
const OrderCustomerCard = ({ customer, alwaysShow = false }) => {
  if (!customer && !alwaysShow) return null;
  const { name, email, _id } = customer || {};
  const customerHref = usePageRecordHref()('customers', _id);
  return (
    <OrderCard title="Customer">
      <Item className="border border-gray-200 dark:border-border rounded-lg px-4 py-3 mt-3">
        <ItemContent className="gap-y-1">
          <ItemTitle className="group text-base flex items-center gap-x-2 whitespace-normal wrap-anywhere">
            <UserIcon
              className="w-4 h-4 text-gray-500 dark:text-muted-foreground shrink-0"
              aria-hidden="true"
            />
            <span className="min-w-0">
              {name || 'Not provided'}
              {name ? <CopyButton text={name} className="ml-1.5 inline-flex align-middle" /> : null}
            </span>
          </ItemTitle>
          <ItemDescription className="group text-sm flex items-center gap-x-2 whitespace-normal wrap-anywhere">
            <EnvelopeIcon
              className="w-4 h-4 text-gray-500 dark:text-muted-foreground shrink-0"
              aria-hidden="true"
            />
            {email ? (
              <span className="min-w-0 wrap-anywhere">
                <Link to={`mailto:${email}`} target="_blank" className="text-foreground">
                  {email}
                </Link>
                <CopyButton text={email} className="ml-1.5 inline-flex align-middle" />
              </span>
            ) : (
              'Not provided'
            )}
          </ItemDescription>
        </ItemContent>
        {customer ? (
          <ItemAction>
            <Button variant="outline" asChild>
              <Link bare to={customerHref}>
                View customer
              </Link>
            </Button>
          </ItemAction>
        ) : null}
      </Item>
    </OrderCard>
  );
};

export default OrderCustomerCard;
